import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import MemberTicketsDataTable from "../components/MemberTicketsDataTable";
import "../assets/ticketsMemberCalendar.module.css";

// Drag-and-drop item types
const ItemTypes = {
  TICKET: 'ticket'
};

// Draggable Ticket Component
const DraggableTicket = ({ ticket, onRemove }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TICKET,
    item: { ticket },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className="event-in-filter ui-draggable"
      id={`unassigned-ticket-${ticket.id}`}
      data-id={ticket.id}
      data-contact-id={ticket.contractor_contact_id}
      style={{
        backgroundColor: ticket.color || 'black',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        padding: '5px 10px',
        margin: '5px 0',
        color: 'white',
        borderRadius: '3px',
        fontSize: '12px'
      }}
    >
      {ticket.id} - {ticket.title_unassigned || ticket.description}
    </div>
  );
};

// Calendar Trash Component
const CalendarTrash = ({ onDrop }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.TICKET,
    drop: (item) => onDrop(item.ticket),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      id="calendarTrash"
      className="panel panel-default panel-standard"
      style={{
        backgroundColor: isOver ? '#ffdddd' : 'transparent',
        border: isOver ? '2px dashed #ff0000' : '1px solid #ddd'
      }}
    >
      <div className="panel-heading panel-title">
        <i className="fa fa-trash" aria-hidden="true"></i>
      </div>
      <div className="panel-body">
        Drop a ticket here to remove it from the calendar
      </div>
    </div>
  );
};

// Edit Ticket Modal Component - Matches Laravel exactly
const EditTicketModal = ({ show, ticket, onClose, onSave, onDelete, contractors, contractorContacts }) => {
  const [formData, setFormData] = useState({
    id: null,
    contractor_id: '',
    contractor_contact_id: '',
    equipment_purchased: ''
  });
  const [ticketDetails, setTicketDetails] = useState({
    title: '',
    status: '',
    contractor_contact: '',
    contractor_decision: '',
    description: ''
  });
  const [filteredContacts, setFilteredContacts] = useState([]);

  const filterContactsByContractor = useCallback((contractorId) => {
    // In a real implementation, this would filter based on contractor_id
    // For now, show all contacts
    setFilteredContacts(contractorContacts || []);
  }, [contractorContacts]);

  const loadTicketDetails = useCallback(async (ticketId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/get?id=${ticketId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const ticketData = data.ticket || data;

        // Set form data (editable fields)
        setFormData({
          id: ticketData.id,
          contractor_id: ticketData.contractor_id || '',
          contractor_contact_id: ticketData.contractor_contact_id || '',
          equipment_purchased: ticketData.equipment_purchased || ''
        });

        // Set details data (readonly fields)
        setTicketDetails({
          title: ticketData.title || `Ticket #${ticketData.id}`,
          status: ticketData.status?.name || ticketData.status_name || '-',
          contractor_contact: ticketData.contractor_contact?.name || '-',
          contractor_decision: ticketData.contractor_decision || '-',
          description: ticketData.description || '-'
        });

        // Filter contractor contacts if contractor is selected
        if (ticketData.contractor_id) {
          filterContactsByContractor(ticketData.contractor_id);
        }
      }
    } catch (error) {
      console.error('Error loading ticket details:', error);
    }
  }, [filterContactsByContractor]);

  useEffect(() => {
    if (ticket && ticket.id) {
      // Load full ticket data from server
      loadTicketDetails(ticket.id);
    }
  }, [ticket, loadTicketDetails]);

  const handleContractorChange = (e) => {
    const contractorId = e.target.value;
    setFormData({...formData, contractor_id: contractorId, contractor_contact_id: ''});
    filterContactsByContractor(contractorId);
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
            <h4 className="modal-title">Ticket</h4>
          </div>

          <div className="modal-body">
            {/* Section 1: Editable Fields (Laravel: tickets.member.fields) */}
            <form className="modal-form">
              <input name="id" type="hidden" value={formData.id || ''} />

              <fieldset className="form-horizontal">
                {/* Contractor Select */}
                <div className="form-group">
                  <label className="control-label col-sm-4">Contractor</label>
                  <div className="col-sm-8">
                    <select
                      id="contractor"
                      className="form-control"
                      value={formData.contractor_id}
                      onChange={handleContractorChange}
                    >
                      <option value="">Please select</option>
                      {contractors && contractors.map((contractor) => (
                        <option key={contractor.id} value={contractor.id}>
                          {contractor.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Contractor Contact Select */}
                <div className="form-group">
                  <label className="control-label col-sm-4">Contractor Contact</label>
                  <div className="col-sm-8">
                    <select
                      id="contractor-contact"
                      className="form-control"
                      value={formData.contractor_contact_id}
                      onChange={(e) => setFormData({...formData, contractor_contact_id: e.target.value})}
                    >
                      <option value="">Please select</option>
                      {filteredContacts.map((contact) => (
                        <option key={contact.id} value={contact.id}>
                          {contact.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Equipment Purchased */}
                <div className="form-group">
                  <label className="control-label col-sm-4">Equipment Purchased</label>
                  <div className="col-sm-8">
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.equipment_purchased}
                      onChange={(e) => setFormData({...formData, equipment_purchased: e.target.value})}
                    />
                  </div>
                </div>
              </fieldset>

              {/* Save Button (inline with form) */}
              <div className="form-group pull-right inline-block">
                <button
                  type="button"
                  className="btn btn-head btn-standard modal-submit-button"
                  onClick={() => onSave(formData)}
                >
                  Save
                </button>
              </div>
            </form>

            {/* Section 2: Details Table (Laravel: tickets.member.details) */}
            <div style={{ clear: 'both' }}>
              <table className="ticket-details-in-calendar-modal" style={{ width: '100%', marginTop: '20px' }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 'bold', width: '40%' }}>Title</td>
                    <td>{ticketDetails.title}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Status</td>
                    <td>{ticketDetails.status}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>C. Contact</td>
                    <td>{ticketDetails.contractor_contact}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Contractor's decision</td>
                    <td>{ticketDetails.contractor_decision}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Description</td>
                    <td>{ticketDetails.description}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Section 3: Action Buttons (Laravel: Show, Edit, Delete) */}
            <div style={{ marginTop: '20px' }}>
              <div className="form-group pull-left inline-block">
                <a
                  href={`/tickets/${formData.id}`}
                  className="btn btn-head btn-standard btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Show
                </a>
                <a
                  href={`/tickets/${formData.id}/edit`}
                  className="btn btn-head btn-standard btn-primary"
                  style={{ marginLeft: '10px' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Edit
                </a>
              </div>

              <div className="form-group pull-right inline-block">
                <button
                  type="button"
                  className="btn btn-head btn-standard bck-red js-modal-delete-button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this ticket?')) {
                      onDelete(formData.id);
                    }
                  }}
                >
                  Delete
                </button>
              </div>

              <div className="clearfix"></div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-default btn-modal-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TicketsMemberCalendar = () => {
  const { userId: userIdFromParams } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const calendarRef = useRef(null);

  // Use userId from params, or fall back to current user's ID
  const userId = userIdFromParams || currentUser?.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [users, setUsers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [contractorContacts, setContractorContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ticketsByLocation, setTicketsByLocation] = useState({});
  const [filteredContactId, setFilteredContactId] = useState('0');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Redirect to user's calendar if userId is missing
  useEffect(() => {
    if (!userId) {
      if (currentUser?.id) {
        navigate(`/tickets/member/${currentUser.id}/calendar`, { replace: true });
      } else {
        setError("User ID is required. Please log in again.");
        setLoading(false);
      }
    }
  }, [userId, currentUser, navigate]);

  const loadAllData = useCallback(async () => {
    // Don't load if userId is missing
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load calendar events and user info
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/tickets/member/${userId}/calendar-data`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to load data: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to load calendar data');
      }

      setUser(data.user);
      setCalendarEvents(data.events || []);
      setStatusCounts(data.statusCounts || {});
      setUsers(data.users || []);
      setContractors(data.contractors || []);
      setContractorContacts(data.contractorContacts || []);
      setLocations(data.locations || []);
      setTicketsByLocation(data.ticketsByLocation || {});

    } catch (error) {
      console.error("Error loading calendar data:", error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleEventDrop = async ({ event, oldEvent }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${event.id}/update-calendar`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({
            start: event.start,
            end: event.end,
            assigned_member_id: userId
          })
        }
      );

      if (!response.ok) throw new Error('Failed to update ticket');

      console.log('Ticket updated successfully');
      await loadAllData();

    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Failed to update ticket. Please try again.');
      // Revert the event
      await loadAllData();
    }
  };

  const handleUnassignedTicketDrop = async (info) => {
    // This is triggered when dropping from external source
    const ticketId = info.draggedEl.dataset.id;
    const dropDate = info.date;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/unassigned/add`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({
            id: ticketId,
            start: dropDate.toISOString(),
            end: dropDate.toISOString(),
            assigned_member_id: userId
          })
        }
      );

      if (!response.ok) throw new Error('Failed to assign ticket');

      // Refresh data
      await loadAllData();

    } catch (error) {
      console.error('Error assigning ticket:', error);
      alert('Failed to assign ticket. Please try again.');
    }
  };

  const handleTrashDrop = async (ticket) => {
    if (!window.confirm(`Remove ticket #${ticket.id} from calendar?`)) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/calendar/ticket/destroy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify({ id: ticket.id })
        }
      );

      if (!response.ok) throw new Error('Failed to remove ticket');

      // Refresh data
      await loadAllData();

    } catch (error) {
      console.error('Error removing ticket:', error);
      alert('Failed to remove ticket. Please try again.');
    }
  };

  const handleEventClick = (clickInfo) => {
    setSelectedTicket(clickInfo.event);
    setShowModal(true);
  };

  const handleSaveTicket = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${formData.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) throw new Error('Failed to update ticket');

      setShowModal(false);
      await loadAllData();

    } catch (error) {
      console.error('Error saving ticket:', error);
      alert('Failed to save ticket. Please try again.');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete ticket');

      setShowModal(false);
      await loadAllData();

    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert('Failed to delete ticket. Please try again.');
    }
  };

  const handleContactFilter = (contactId) => {
    setFilteredContactId(contactId);

    // Filter unassigned tickets by contact
    if (contactId === '0') {
      // Show all
      loadAllData();
    } else if (contactId === '-1') {
      // Show unassigned only
      const filtered = {};
      Object.keys(ticketsByLocation).forEach(locId => {
        filtered[locId] = ticketsByLocation[locId].filter(t => !t.contractor_contact_id);
      });
      setTicketsByLocation(filtered);
    } else {
      // Show specific contact
      const filtered = {};
      Object.keys(ticketsByLocation).forEach(locId => {
        filtered[locId] = ticketsByLocation[locId].filter(t => t.contractor_contact_id === parseInt(contactId));
      });
      setTicketsByLocation(filtered);
    }
  };

  const handleStatusCardClick = (statusId) => {
    setStatusFilter(statusFilter === statusId ? '' : statusId);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center" style={{ padding: '50px' }}>
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading calendar data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button className="btn btn-primary ml-3" onClick={loadAllData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="container-fluid">
        {/* Breadcrumb */}
        <div className="breadcrumb-row">
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate("/dashboard")}
          >
            Dashboard
          </span>
          <span style={{ margin: '0 5px' }}>/</span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate("/tickets")}
          >
            Tickets
          </span>
          <div style={{ background: "#30C693", fontWeight: 400, fontSize: "14px", display: 'inline-block', padding: '2px 8px', marginLeft: '5px' }}>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-properties"> {user?.fullName}'s Calendar</span>
          </div>
        </div>

        <div className="row js-tickets-of-member">
          <div className="col-md-8">
            <div className="panel panel-default panel-standard">
              <div id="ticket-status-span" className="panel-heading panel-title">
                <h3>
                  {user?.fullName}
                  <span className="text-muted">' tickets</span>
                </h3>
              </div>
              <div className="panel-body">
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                  initialView="listWeek"
                  headerToolbar={{
                    left: '',
                    center: 'prev,title,next',
                    right: 'listWeek,dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  events={calendarEvents}
                  editable={true}
                  droppable={true}
                  eventDrop={handleEventDrop}
                  eventClick={handleEventClick}
                  drop={handleUnassignedTicketDrop}
                  slotMinTime="06:00:00"
                  firstDay={1}
                  height="auto"
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-md-4">
            {/* Calendar Trash */}
            <CalendarTrash onDrop={handleTrashDrop} />

            {/* Unassigned Tickets */}
            <div className="panel panel-default panel-standard">
              <div className="panel-heading panel-title">
                <h3>Unassigned tickets</h3>
              </div>
              <div className="panel-body">
                <div>
                  <span><strong>Filter by contact: </strong></span>
                  <select
                    id="calendar_member_filter_contact_select"
                    className="form-control"
                    value={filteredContactId}
                    onChange={(e) => handleContactFilter(e.target.value)}
                  >
                    <option value="0">All</option>
                    <option value="-1">Unassigned</option>
                    {contractorContacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name}
                      </option>
                    ))}
                  </select>
                </div>

                <hr />

                <div id="unassigned-tickets-wrapper">
                  {locations.map((location) => (
                    <div
                      key={location.id}
                      className="event-in-filter-in-group"
                      data-location={location.id}
                    >
                      <div className="event-in-filter-grouped" style={{ fontWeight: 'bold', padding: '5px 0', marginTop: '10px' }}>
                        {location.name}
                      </div>
                      {ticketsByLocation[location.id]?.map((ticket) => (
                        <DraggableTicket
                          key={ticket.id}
                          ticket={ticket}
                          onRemove={handleTrashDrop}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Status Overview */}
          <div className="col-md-12">
            <div className="panel-title title-standard">
              <h3>Status overview</h3>
            </div>
            <div className="panel-group margin-down">
              {Object.entries(statusCounts)
                .filter(([key, data]) => key !== 'all' && data && data.name && data.color)
                .map(([key, data]) => (
                <div key={key} className="col-md-2 col-lg-2">
                  <div
                    className="activity-box js-ticket-activity-box"
                    style={{ backgroundColor: data.color }}
                    data-status_id={data.id}
                    onClick={() => handleStatusCardClick(data.id)}
                  >
                    <p className="activity-value">{data.count}</p>
                    <p className="activity-title">{data.name}</p>
                  </div>
                </div>
              ))}

              <div className="col-md-2 col-lg-2">
                <div
                  className="activity-box js-ticket-activity-box"
                  style={{ backgroundColor: "#0a7280" }}
                  onClick={() => handleStatusCardClick('')}
                >
                  <p className="activity-value">{statusCounts.all?.count || 0}</p>
                  <p className="activity-title">ALL</p>
                </div>
              </div>
            </div>
          </div>

          {/* Go to User Calendar */}
          <div className="col-md-12 mb-20">
            <div className="property-viewings-go-to-user-calendar-group" style={{ marginBottom: '20px' }}>
              <span><strong>Go to user calendar: </strong></span>
              <select
                className="form-control js-ticket-go-to-user-calendar"
                style={{ display: 'inline-block', width: 'auto', marginLeft: '10px' }}
                onChange={(e) => {
                  if (e.target.value && e.target.value !== '0') {
                    navigate(`/tickets/member/${e.target.value}/calendar`);
                  }
                }}
                value={userId}
              >
                <option value="0">Please select</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DataTable - Server-side with 18 columns matching Laravel */}
          <div className="col-md-12">
            <div className="panel panel-default panel-standard">
              <div id="property-status-span" className="panel-heading panel-title">
                <h3>List</h3>
              </div>
              <div className="panel-body">
                <MemberTicketsDataTable
                  userId={userId}
                  statusFilter={statusFilter}
                  contractors={contractors}
                  onTicketClick={(ticketId) => {
                    navigate(`/tickets/${ticketId}`);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        <EditTicketModal
          show={showModal}
          ticket={selectedTicket}
          onClose={() => setShowModal(false)}
          onSave={handleSaveTicket}
          onDelete={handleDeleteTicket}
          contractors={contractors}
          contractorContacts={contractorContacts}
        />
      </div>
    </DndProvider>
  );
};

export default TicketsMemberCalendar;
